// @owners { team: patient-health }
import autobind from 'autobind-decorator';
import React from 'react';
import { push } from 'react-router-redux';
// eslint-disable-next-line import/no-deprecated
import { clearInsuranceError, declineInsurance, deleteInsurance } from '~shared/actions/insurances';
// eslint-disable-next-line import/no-deprecated
import { type OnboardingUserUpdate, updateOnboardingUser } from '~shared/actions/onboarding';
import { getInsurances } from '~shared/features/insurances/selectors/getInsurances';
import {
  INSURANCE_INPUT,
  INSURANCE_INPUT_MANUAL,
  INSURANCE_INPUT_PHOTO,
  ONBOARD_STATE_INSURANCE_INPUT,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent, previousWebURL } from '~shared/lib/analytics/src/helper';
import { type Event } from '~shared/lib/analytics/src/types';
import getDeclineInsuranceLoading from '~shared/selectors/ui/loading/getDeclineInsuranceLoading';
import { connect } from '~shared/store';
import { type Insurance, type ReduxStateShared } from '~shared/types';
import OnboardingInsuranceInput from './OnboardingInsuranceInput';
import { nextUrl } from '~web/features/onboarding/helpers/urlHandler';

type Props = {
  readonly clearInsuranceError: () => void;
  // eslint-disable-next-line import/no-deprecated
  readonly declineInsurance: () => void;
  // eslint-disable-next-line import/no-deprecated
  readonly deleteInsurance: (arg0: Insurance) => void;
  readonly declineInsuranceLoading: boolean;
  readonly hasInsurance: boolean;
  readonly manualURL: string;
  readonly navigateTo: (arg0: string) => void;
  readonly origin: string;
  readonly photoURL: string;
  readonly sendAnalyticsEvent: (arg0: Event) => void;
  readonly showProgyny: boolean;
  readonly skipURL: string;
  // eslint-disable-next-line import/no-deprecated
  readonly updateOnboardingUser: (arg0: OnboardingUserUpdate) => void;
};

class OnboardingInsuranceInputContainer extends React.PureComponent<Props> {
  componentDidMount() {
    this.props.updateOnboardingUser({
      onboard_state: ONBOARD_STATE_INSURANCE_INPUT,
    });
  }

  componentWillUnmount() {
    this.props.clearInsuranceError();
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  onUploadPhoto() {
    const { photoURL, navigateTo } = this.props;
    navigateTo(photoURL);
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  onEnterManually() {
    const { manualURL, navigateTo } = this.props;
    navigateTo(manualURL);
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  onDeclineInsurance() {
    const { declineInsurance: declineIns, navigateTo, showProgyny } = this.props;

    this.props.sendAnalyticsEvent(
      createEvent(EVENTS.ONBOARDING__INSURANCE_NONE_CLICKED, {
        is_progyny_rx_view: showProgyny,
      }),
    );

    // @ts-expect-error TS(2339): Property 'then' does not exist on type 'void'.
    // eslint-disable-next-line promise/catch-or-return, @typescript-eslint/no-confusing-void-expression
    declineIns().then((isSuccessful) => {
      // eslint-disable-next-line promise/always-return
      if (isSuccessful) {
        navigateTo(this.props.skipURL);
      }
    });
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  onSkip() {
    const { navigateTo, showProgyny } = this.props;

    this.props.sendAnalyticsEvent(
      createEvent(EVENTS.ONBOARDING__INSURANCE_REMINDER_CLICKED, {
        is_progyny_rx_view: showProgyny,
      }),
    );

    navigateTo(this.props.skipURL);
  }

  render() {
    return (
      <OnboardingInsuranceInput
        {...this.props}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onUploadPhoto={this.onUploadPhoto}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onEnterManually={this.onEnterManually}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onDeclineInsurance={this.onDeclineInsurance}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onSkip={this.onSkip}
      />
    );
  }
}

const mapStateToProps = (state: ReduxStateShared) => {
  const previousUrl = previousWebURL(state);
  const origin = previousUrl ? routeFromPath(previousUrl) : '';
  return {
    origin,
    showProgyny: getShowProgynySelfServiceForUser(state),
    declineInsuranceLoading: getDeclineInsuranceLoading(state),
    photoURL: nextUrl(state, INSURANCE_INPUT, INSURANCE_INPUT_PHOTO),
    manualURL: nextUrl(state, INSURANCE_INPUT, INSURANCE_INPUT_MANUAL),
    skipURL: nextUrl(state, INSURANCE_INPUT),
    hasInsurance: !!getInsurances(state)?.length,
  };
};

const mapDispatchToProps = {
  // eslint-disable-next-line import/no-deprecated
  updateOnboardingUser,
  navigateTo: push,
  clearInsuranceError,
  // eslint-disable-next-line import/no-deprecated
  declineInsurance,
  // eslint-disable-next-line import/no-deprecated
  deleteInsurance,
  sendAnalyticsEvent,
};

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingInsuranceInputContainer);
